import { OidcClientTs, getLoginSession, log, logout, sessionStorageToOidcStorage } from "@scaleaq/auth-client";

import { LogLevelFilter, createConsoleOutput, createLogger, createWebLogOutput } from "@scaleaq/logger";
import { type IScaleLogger } from "@scaleaq/logger/lib/types";
import { getGlobalConfig } from "src/datahandling/redux/globalConfig";
import { type Config } from "src/datahandling/redux/slice/config/types";
import { type Environment } from "src/models/types/common.types";
import { isInDev, GET_ENV } from "src/utility";

const CONSOLE_OUTPUT_NAME = "console";
const WEBLOG_OUTPUT_NAME = "weblog";

let genericLogger: IScaleLogger | undefined;

let pressumedEnv: null | Environment = null;

export function getGenericLogger() {
	const currentEnv = GET_ENV();

	if (!genericLogger || pressumedEnv !== currentEnv) {
		pressumedEnv = currentEnv;

		const consoleWrapper = createConsoleOutput({ outputName: CONSOLE_OUTPUT_NAME });

		const weblog = createWebLogOutput({
			outputName: WEBLOG_OUTPUT_NAME,
			enviroment: GET_ENV,
			application: "knowledger-web",
			applicationVersion: getGlobalConfig()?.APPLICATION?.VERSION ?? "unknown",
			authTokenFactory: getAccessToken,
		});

		const outputLogLevels = {
			default: LogLevelFilter.Warning,
			//TODO change this back to error after done with debuging
			[CONSOLE_OUTPUT_NAME]: isInDev() ? LogLevelFilter.Debug : LogLevelFilter.Warning,
			[WEBLOG_OUTPUT_NAME]: LogLevelFilter.Error,
		};

		const categoryLogLevels = {
			"@scaleaq/auth-client": LogLevelFilter.Warning,
		};

		genericLogger = createLogger({
			outputs: [consoleWrapper, weblog],
			outputLogLevels: outputLogLevels,
			categoryLogLevels,
		});
	}

	return genericLogger;
}

let oidcClient: OidcClientTs.OidcClient | undefined;

export async function logoutInsightsLive() {
	if (!oidcClient) return;
	await logout(oidcClient, { post_logout_redirect_uri: location.origin });
}

function getOidcClient(configParams?: Config["B2C"]) {
	if (!oidcClient) {
		if (!configParams) return null;

		const { CLIENT_ID, TENANT, POLICY, SCOPES } = configParams;
		oidcClient = new OidcClientTs.OidcClient({
			client_id: CLIENT_ID,
			authority: `https://${TENANT}.b2clogin.com/${TENANT}.onmicrosoft.com/${POLICY}/v2.0`,
			redirect_uri: typeof location === "object" ? location.origin : "/",
			response_type: "code",
			response_mode: "query",
			scope: SCOPES.join(" "),
			stateStore: sessionStorageToOidcStorage(),
		});
	}

	log.setLogger(getGenericLogger());
	log.setLevel(LogLevelFilter.Information);

	return oidcClient;
}

export async function fetchLoginSession(configParams: Config["B2C"]) {
	const client = getOidcClient(configParams);

	if (!client) {
		return null;
	}

	const {
		access_token,
		profile: { sub, name },
		expires_on,
	} = await getLoginSession(client);

	return { access_token, userId: sub, expires_on, name };
}

export async function getAccessToken() {
	const client = getOidcClient();

	if (!client) {
		return "";
	}

	const { access_token } = await getLoginSession(client);

	return access_token;
}
